/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import { Box, Drawer, Hidden, List, ListSubheader, makeStyles } from '@material-ui/core';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';

const renderNavItems = ({ items, pathname, depth }) => {
  const activePartnerId = localStorage.getItem('activePartnerId');
  const activeDistributorId = localStorage.getItem('activeDistributorId');
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) =>
          reduceChildRoutes({
            acc,
            item,
            pathname,
            depth,
            activePartnerId,
            activeDistributorId
          }),
        []
      )}
    </List>
  );
};

const filterItemsByPermissions = (items, account) =>
  items.reduce((acc, item) => {
    if (item.items) {
      const filteredChildren = filterItemsByPermissions(item.items, account);
      return filteredChildren.length > 0 ? [...acc, { ...item, items: filteredChildren }] : acc;
    }

    const showItem = Array.isArray(item.permissions)
      ? account.permissions.some((row) => item.permissions.includes(row))
      : true;
    return showItem ? [...acc, item] : acc;
  }, []);

const reduceChildRoutes = ({
  acc,
  pathname,
  item: { title, items: itemChildren, icon, info, external, href: itemHref },
  depth = 0,
  activePartnerId,
  activeDistributorId
}) => {
  const key = title + depth;
  const hasChildren = itemChildren;

  const isOpen =
    hasChildren &&
    itemChildren.some((navChildItem) =>
      matchPath(pathname, {
        path: navChildItem.href,
        exact: false
      })
    );

  if (title === 'API Docs' && activeDistributorId) {
    return [...acc];
  }

  let href = itemHref;
  if (typeof itemHref === 'function') {
    href = itemHref({ activePartnerId, activeDistributorId });
  }

  const navItem = (
    <NavItem
      depth={depth}
      icon={icon}
      key={key}
      info={info}
      title={title}
      external={external}
      {...(hasChildren ? { open: Boolean(isOpen) } : { href })}
    >
      {hasChildren &&
        renderNavItems({
          depth: depth + 1,
          pathname,
          items: itemChildren
        })}
    </NavItem>
  );

  return [...acc, navItem];
};

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256,
    overflowY: 'visible !important'
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
    overflowY: 'visible !important'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ openMobile, onMobileClose, account, navConfig, openDesktop }) => {
  const [displayedMenu, setDisplayedMenu] = useState([]);
  const classes = useStyles();
  const { pathname } = useLocation();
  const activePartnerId = localStorage.getItem('activePartnerId');
  const activeDistributorId = localStorage.getItem('activeDistributorId');

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [pathname]);

  useEffect(() => {
    setDisplayedMenu(filterItemsByPermissions(navConfig, account));
  }, [account, navConfig]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }} className={classes.psContainerOverride}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink
              to={
                account.user?.type === 'INTERNAL' || activePartnerId || activeDistributorId
                  ? '/app/reports/dashboard'
                  : '/app/reports/home'
              }
            >
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          {displayedMenu.map((menuItem) => (
            <List
              key={menuItem.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {menuItem.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({ items: menuItem.items, pathname })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer anchor="left" classes={{ paper: classes.desktopDrawer }} open={openDesktop} variant="persistent">
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  account: PropTypes.object.isRequired,
  openDesktop: PropTypes.bool.isRequired
};

export default NavBar;
